/**
 * EditWODialog — Edit existing Work Order before JC issue
 * 
 * Features:
 * - Pre-populated with existing WO materials and processes
 * - Locked rows for issued materials/processes
 * - Edit history tracking
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { 
  Loader2, Plus, Trash2, ChevronUp, ChevronDown, 
  Lock, Package, Settings, Save
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Props:
 * @param {boolean} open - Dialog open state
 * @param {function} onOpenChange - Dialog open state handler
 * @param {object} workOrder - The WO object to edit
 * @param {function} onSuccess - Callback after successful edit
 */
export default function EditWODialog({
  open,
  onOpenChange,
  workOrder,
  onSuccess
}) {
  // Form state
  const [woNotes, setWoNotes] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Material/Process state
  const [materials, setMaterials] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Material management
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [itemOptions, setItemOptions] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  
  // Process management
  const [showAddProcess, setShowAddProcess] = useState(false);
  const [processOptions, setProcessOptions] = useState([]);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);

  // Initialize from WO when dialog opens
  useEffect(() => {
    if (open && workOrder) {
      setWoNotes(workOrder.notes || workOrder.remarks || '');
      setHasChanges(false);
      
      // Initialize materials with tracking fields
      const mats = (workOrder.material_blocking || []).map((m, idx) => ({
        ...m,
        _key: `wo-mat-${idx}`,
        required_qty: m.required_quantity || m.blocked_quantity || 0,
        bom_qty_per_unit: m.bom_quantity || 0,
        unit_price: m.price || 0,
        is_from_bom: m.is_from_bom !== false,
        is_substituted: m.is_substituted || false,
        original_item_id: m.original_item_id || null,
        _isLocked: (m.issued_quantity || 0) > 0  // Lock if any qty issued
      }));
      setMaterials(mats);
      
      // Initialize processes with tracking fields
      const procs = (workOrder.production_processes || []).map((p, idx) => ({
        ...p,
        _key: `wo-proc-${idx}`,
        is_from_bom: p.is_from_bom !== false,
        _isLocked: (p.issued_quantity || 0) > 0  // Lock if JC issued
      }));
      setProcesses(procs);
    }
  }, [open, workOrder]);

  // Material handlers
  const handleMaterialQtyChange = (key, value) => {
    setMaterials(prev => prev.map(m => {
      if (m._key === key && !m._isLocked) {
        const newQty = parseFloat(value) || 0;
        setHasChanges(true);
        return {
          ...m,
          required_qty: newQty,
          blocked_quantity: newQty
        };
      }
      return m;
    }));
  };

  const handleRemoveMaterial = (key) => {
    const mat = materials.find(m => m._key === key);
    if (mat?._isLocked) {
      toast.error('Cannot remove material with issued quantity');
      return;
    }
    if (mat?.is_from_bom) {
      if (!window.confirm('This material is in the BOM. Removing it means production won\'t receive it. Continue?')) {
        return;
      }
    }
    setMaterials(prev => prev.filter(m => m._key !== key));
    setHasChanges(true);
  };

  const handleAddMaterial = async () => {
    if (!loadingItems && itemOptions.length === 0) {
      await fetchItemOptions();
    }
    setShowAddMaterial(true);
  };

  const fetchItemOptions = async () => {
    try {
      setLoadingItems(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/items?category=RMT&limit=500`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const options = response.data.map(item => ({
        value: item.id,
        label: `${item.item_code} - ${item.name}`,
        code: item.item_code || '',
        category: item.category || '',
        searchTerms: [item.item_code, item.name],
        item
      }));
      setItemOptions(options);
    } catch (error) {
      toast.error('Failed to load items');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleSelectMaterial = (itemId) => {
    const option = itemOptions.find(o => o.value === itemId);
    if (!option) return;
    
    const item = option.item;
    const newMaterial = {
      _key: `added-${Date.now()}`,
      item_id: item.id,
      item_code: item.item_code,
      item_name: item.name,
      unit: item.unit || 'NOS',
      bom_qty_per_unit: 0,
      required_qty: 0,
      blocked_quantity: 0,
      issued_quantity: 0,
      available_stock: item.stock_in_hand || 0,
      unit_price: item.purchase_price || item.price || 0,
      is_from_bom: false,
      is_substituted: false,
      original_item_id: null,
      process_index: 0,
      _isLocked: false
    };
    
    setMaterials(prev => [...prev, newMaterial]);
    setShowAddMaterial(false);
    setHasChanges(true);
  };

  // Process handlers
  const handleProcessChange = (key, field, value) => {
    setProcesses(prev => prev.map(p => {
      if (p._key === key && !p._isLocked) {
        setHasChanges(true);
        return { ...p, [field]: value };
      }
      return p;
    }));
  };

  const handleRemoveProcess = (key) => {
    const proc = processes.find(p => p._key === key);
    if (proc?._isLocked) {
      toast.error('Cannot remove process with issued Job Card');
      return;
    }
    if (proc?.is_from_bom) {
      if (!window.confirm('This process is in the BOM. Removing it will skip this step. Continue?')) {
        return;
      }
    }
    setProcesses(prev => prev.filter(p => p._key !== key));
    setHasChanges(true);
  };

  const handleMoveProcess = (key, direction) => {
    const proc = processes.find(p => p._key === key);
    if (proc?._isLocked) {
      toast.error('Cannot reorder locked processes');
      return;
    }
    
    setProcesses(prev => {
      const idx = prev.findIndex(p => p._key === key);
      if (idx === -1) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === prev.length - 1) return prev;
      
      const newArr = [...prev];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      [newArr[idx], newArr[swapIdx]] = [newArr[swapIdx], newArr[idx]];
      
      return newArr.map((p, i) => ({ ...p, process_seq: i + 1, sequence: i + 1 }));
    });
    setHasChanges(true);
  };

  const handleAddProcess = async () => {
    if (!loadingProcesses && processOptions.length === 0) {
      await fetchProcessOptions();
    }
    setShowAddProcess(true);
  };

  const fetchProcessOptions = async () => {
    try {
      setLoadingProcesses(true);
      const token = localStorage.getItem('token');
      const [procRes, suppRes] = await Promise.all([
        axios.get(`${API_URL}/processes?limit=200`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/suppliers?limit=500`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setProcessOptions(procRes.data.map(p => ({
        value: p.id,
        label: p.name,
        process: p
      })));
      
      setSupplierOptions(suppRes.data.map(s => ({
        value: s.id,
        label: s.name
      })));
    } catch (error) {
      toast.error('Failed to load processes');
    } finally {
      setLoadingProcesses(false);
    }
  };

  const handleSelectProcess = (processId) => {
    const option = processOptions.find(o => o.value === processId);
    if (!option) return;
    
    const proc = option.process;
    const newProcess = {
      _key: `added-proc-${Date.now()}`,
      process_id: proc.id,
      process_name: proc.name,
      sequence: processes.length + 1,
      process_seq: processes.length + 1,
      is_outsourced: false,
      jobber_id: null,
      jobber_name: null,
      benchmark_days: proc.benchmark_days || 0,
      is_from_bom: false,
      issued_quantity: 0,
      completed_quantity: 0,
      status: 'not_started',
      checklist: proc.checklist?.map(c => typeof c === 'string' ? c : c.text) || [],
      _isLocked: false
    };
    
    setProcesses(prev => [...prev, newProcess]);
    setShowAddProcess(false);
    setHasChanges(true);
  };

  // Calculate totals
  const totalMaterialCost = materials.reduce((sum, m) => 
    sum + ((m.required_qty || 0) * (m.unit_price || 0)), 0);

  // Save handler
  const handleSave = async () => {
    if (!hasChanges && woNotes === (workOrder?.notes || workOrder?.remarks || '')) {
      toast.info('No changes to save');
      onOpenChange(false);
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        notes: woNotes
      };
      
      // Include materials if any were modified
      payload.materials = materials.map(m => ({
        item_id: m.item_id,
        item_name: m.item_name,
        item_code: m.item_code || '',
        unit: m.unit || 'NOS',
        bom_qty_per_unit: m.bom_qty_per_unit || 0,
        required_quantity: m.required_qty || 0,
        unit_price: m.unit_price || 0,
        is_from_bom: m.is_from_bom,
        is_substituted: m.is_substituted || false,
        original_item_id: m.original_item_id || null,
        process_index: m.process_index || 0
      }));
      
      // Include processes
      payload.processes = processes.map((p, idx) => ({
        process_id: p.process_id || null,
        process_name: p.process_name,
        sequence: idx + 1,
        is_outsourced: p.is_outsourced || false,
        jobber_id: p.jobber_id || null,
        jobber_name: p.jobber_name || null,
        benchmark_days: p.benchmark_days || 0,
        is_from_bom: p.is_from_bom,
        checklist: p.checklist?.map(c => typeof c === 'string' ? c : c.text) || []
      }));
      
      const response = await axios.patch(
        `${API_URL}/work-orders/${workOrder.id}/edit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Work Order ${workOrder.work_order_number} updated`);
        if (response.data.changes?.length > 0) {
          toast.info(`Changes: ${response.data.changes.join(', ')}`, { duration: 4000 });
        }
        onOpenChange(false);
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update Work Order';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setSaving(false);
    }
  };

  if (!workOrder) return null;

  const canEdit = workOrder.status === 'created' || workOrder.status === 'issued';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[95vh] flex flex-col" data-testid="edit-wo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5 text-indigo-600" />
            Edit Work Order: {workOrder.work_order_number}
          </DialogTitle>
          <DialogDescription>
            {workOrder.item_name} | Qty: {workOrder.quantity} | Status: {workOrder.status}
            {!canEdit && <span className="text-red-500 ml-2">(Read-only - WO is {workOrder.status})</span>}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6">
            {/* Materials Section */}
            <div className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                <h3 className="font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Materials ({materials.length})
                </h3>
                {canEdit && (
                  <Button variant="ghost" size="sm" onClick={handleAddMaterial} data-testid="edit-add-material-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Material
                  </Button>
                )}
              </div>
              
              {materials.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">No materials</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Material</th>
                        <th className="text-left px-3 py-2 font-medium w-16">Unit</th>
                        <th className="text-right px-3 py-2 font-medium w-24">Req Qty</th>
                        <th className="text-right px-3 py-2 font-medium w-20">Issued</th>
                        <th className="text-right px-3 py-2 font-medium w-24">Value</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {materials.map((mat) => (
                        <tr key={mat._key} className={`border-b last:border-0 ${mat._isLocked ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              {mat._isLocked && <Lock className="h-3 w-3 text-slate-400" />}
                              <span className="font-medium">{mat.item_name}</span>
                              {!mat.is_from_bom && <Badge variant="outline" className="text-blue-600">Added</Badge>}
                            </div>
                            <div className="text-xs text-slate-500">{mat.item_code}</div>
                          </td>
                          <td className="px-3 py-2 text-slate-600">{mat.unit}</td>
                          <td className="px-3 py-2">
                            {mat._isLocked || !canEdit ? (
                              <span className="text-right block">{mat.required_qty?.toFixed(2)}</span>
                            ) : (
                              <Input
                                type="number"
                                value={mat.required_qty || ''}
                                onChange={(e) => handleMaterialQtyChange(mat._key, e.target.value)}
                                className="w-24 h-8 text-right"
                                min={0}
                                step="0.01"
                              />
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-500">{mat.issued_quantity || 0}</td>
                          <td className="px-3 py-2 text-right">
                            {((mat.required_qty || 0) * (mat.unit_price || 0)).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                          </td>
                          <td className="px-3 py-2">
                            {canEdit && !mat._isLocked && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveMaterial(mat._key)}
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {showAddMaterial && (
                <div className="p-4 border-t bg-blue-50">
                  <div className="flex items-center gap-2">
                    <SearchableSelect
                      options={itemOptions}
                      value=""
                      onValueChange={handleSelectMaterial}
                      placeholder="Search and select material..."
                      searchPlaceholder="Search by code or name..."
                      className="flex-1"
                      disabled={loadingItems}
                    />
                    <Button variant="ghost" size="sm" onClick={() => setShowAddMaterial(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Processes Section */}
            <div className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                <h3 className="font-medium flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Processes ({processes.length})
                </h3>
                {canEdit && (
                  <Button variant="ghost" size="sm" onClick={handleAddProcess} data-testid="edit-add-process-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Process
                  </Button>
                )}
              </div>
              
              {processes.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">No processes</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-center px-2 py-2 font-medium w-10">#</th>
                        <th className="text-left px-3 py-2 font-medium">Process</th>
                        <th className="text-center px-3 py-2 font-medium w-28">Type</th>
                        <th className="text-left px-3 py-2 font-medium w-40">Jobber</th>
                        <th className="text-right px-3 py-2 font-medium w-20">Days</th>
                        <th className="text-right px-3 py-2 font-medium w-20">Issued</th>
                        <th className="w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {processes.map((proc, idx) => (
                        <tr key={proc._key} className={`border-b last:border-0 ${proc._isLocked ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                          <td className="px-2 py-2 text-center text-slate-500">{idx + 1}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              {proc._isLocked && <Lock className="h-3 w-3 text-slate-400" />}
                              <span className="font-medium">{proc.process_name}</span>
                              {!proc.is_from_bom && <Badge variant="outline" className="text-blue-600">Ad-hoc</Badge>}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {proc._isLocked || !canEdit ? (
                              <Badge variant={proc.is_outsourced ? 'secondary' : 'outline'}>
                                {proc.is_outsourced ? 'OJC' : 'In-house'}
                              </Badge>
                            ) : (
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-xs text-slate-500">In</span>
                                <Switch
                                  checked={proc.is_outsourced}
                                  onCheckedChange={(checked) => handleProcessChange(proc._key, 'is_outsourced', checked)}
                                />
                                <span className="text-xs text-slate-500">OJC</span>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {proc.is_outsourced ? (
                              proc._isLocked || !canEdit ? (
                                <span>{proc.jobber_name || '-'}</span>
                              ) : (
                                <SearchableSelect
                                  options={supplierOptions}
                                  value={proc.jobber_id || ''}
                                  onValueChange={(val) => {
                                    const opt = supplierOptions.find(o => o.value === val);
                                    handleProcessChange(proc._key, 'jobber_id', val);
                                    handleProcessChange(proc._key, 'jobber_name', opt?.label || '');
                                  }}
                                  placeholder="Select jobber..."
                                  className="h-8"
                                />
                              )
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {proc._isLocked || !canEdit ? (
                              <span className="text-right block">{proc.benchmark_days || 0}</span>
                            ) : (
                              <Input
                                type="number"
                                value={proc.benchmark_days || ''}
                                onChange={(e) => handleProcessChange(proc._key, 'benchmark_days', parseFloat(e.target.value) || 0)}
                                className="w-16 h-8 text-right"
                                min={0}
                                step="0.5"
                              />
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-500">{proc.issued_quantity || 0}</td>
                          <td className="px-3 py-2">
                            {canEdit && !proc._isLocked && (
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMoveProcess(proc._key, 'up')}
                                  disabled={idx === 0}
                                  className="h-7 w-7 p-0"
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMoveProcess(proc._key, 'down')}
                                  disabled={idx === processes.length - 1}
                                  className="h-7 w-7 p-0"
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveProcess(proc._key)}
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {showAddProcess && (
                <div className="p-4 border-t bg-blue-50">
                  <div className="flex items-center gap-2">
                    <SearchableSelect
                      options={processOptions}
                      value=""
                      onValueChange={handleSelectProcess}
                      placeholder="Search and select process..."
                      searchPlaceholder="Search processes..."
                      className="flex-1"
                      disabled={loadingProcesses}
                    />
                    <Button variant="ghost" size="sm" onClick={() => setShowAddProcess(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Cost Summary */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex gap-8 text-sm">
                <div>
                  <span className="text-slate-500">Total Material Cost:</span>
                  <span className="ml-2 font-semibold">
                    {totalMaterialCost.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Cost Per Unit:</span>
                  <span className="ml-2 font-semibold">
                    {(totalMaterialCost / (workOrder.quantity || 1)).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-700">Notes</label>
              <Textarea
                value={woNotes}
                onChange={(e) => { setWoNotes(e.target.value); setHasChanges(true); }}
                placeholder="Add notes for this Work Order..."
                className="mt-1"
                rows={2}
                disabled={!canEdit}
                data-testid="edit-wo-notes-input"
              />
            </div>

            {/* Edit History */}
            {workOrder.edit_history && workOrder.edit_history.length > 0 && (
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">Edit History</h4>
                <div className="space-y-2 text-sm">
                  {workOrder.edit_history.map((entry, idx) => (
                    <div key={idx} className="text-slate-600">
                      <span className="text-slate-400">{new Date(entry.edited_at).toLocaleString()}</span>
                      <span className="mx-2">-</span>
                      <span>{entry.edited_by_name}</span>
                      <span className="mx-2">:</span>
                      <span>{entry.changes_summary}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          {canEdit && (
            <Button
              onClick={handleSave}
              disabled={saving || (!hasChanges && woNotes === (workOrder?.notes || workOrder?.remarks || ''))}
              data-testid="save-wo-edit-btn"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" />Save Changes</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
